import { Component, OnInit, Self } from "@angular/core";
import { EntityModalComponent } from "../../../util/components/modal/entity-modal.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { NameDto, ReportDto } from "app/api/models";
import { CaslAction } from "app/server/enums/casl-action.enum";
import { EntityTyp } from "app/util/enums/entity-typ.enum";
import { ClientService, EmployeeService, HousingService } from "app/api/services";
import { ReportFormGroupService } from "../report-form-group.service";
import { OfficeType } from "app/server/enums/office-type.enum";
import { ReportType } from "app/server/enums/report-type.enum";
import { UserRole } from "app/server/enums/user-role.enum";
import { AuthService } from "../../../util/service/auth.service";
import { FormArray } from "@angular/forms";
import { EnumDropdownOption, EnumService } from "../../../util/service/enum.service";
import { ReportSubject } from "../../../server/enums/report-subject.enum";
import { ReportEntryCategory } from "../../../server/enums/report-entry-category.enum";

@Component({
  selector: "app-report-modal",
  templateUrl: "./report-modal.component.html",
  styleUrl: "./report-modal.component.scss",
  providers: [ReportFormGroupService],
  standalone: false,
})
export class ReportModalComponent extends EntityModalComponent<ReportDto> implements OnInit {
  protected readonly CaslAction = CaslAction;
  protected readonly EntityTyp = EntityTyp;
  protected readonly UserRole = UserRole;

  subjects!: EnumDropdownOption[];
  allSubjects!: EnumDropdownOption[];
  housing!: NameDto[];
  employees!: NameDto[];
  clients!: NameDto[];
  reportEntryCategories!: ReportEntryCategory[];
  reportEntryCategoryOptions!: EnumDropdownOption[];

  isHousingReport = false;
  isKJHReport = false;

  get isAdmin() {
    return this.authService.currentUserValue?.role === UserRole.Admin;
  }

  constructor(
    @Self() public formGroupService: ReportFormGroupService,
    public modal: NgbActiveModal,
    private housingService: HousingService,
    private employeeService: EmployeeService,
    private clientService: ClientService,
    public authService: AuthService,
    private enumService: EnumService
  ) {
    super();
  }

  override ngOnInit(): void {
    this.allSubjects = this.enumService.reportSubjectDropdownOptions;
    this.formGroupService.offices = this.data["offices"];

    super.ngOnInit();

    if (!this.formGroup.get("officeId").value && this.formGroupService.offices.length === 1) {
      this.formGroup.get("officeId").setValue(this.formGroupService.offices[0].id);
    }
    this.updateForm({
      officeId: this.formGroup.get("officeId").value,
      housingId: this.formGroup.get("housingId").value,
      initial: true,
      clearBerichtseintraege: false,
    });

    this.formGroup.get("officeId").valueChanges.subscribe((officeId) => {
      this.formGroup.get("housingId")?.setValue(null);
      this.updateForm({ officeId, initial: false, clearBerichtseintraege: true });
    });
    this.formGroup.get("housingId").valueChanges.subscribe((housingId) => {
      this.updateForm({
        officeId: this.formGroup.get("officeId").value,
        housingId,
        initial: false,
        clearBerichtseintraege: false,
      });
    });
    this.formGroup.get("mainEmployeeId").valueChanges.subscribe((mainEmployeeId) => {
      const otherEmployeesFormArray = this.formGroup.get("otherEmployees") as FormArray;
      const otherEmployees = otherEmployeesFormArray.value;
      otherEmployeesFormArray.setValue(otherEmployees.filter((oe) => oe !== mainEmployeeId));
    });
  }

  private updateForm(selection?: {
    officeId?: number;
    housingId?: number;
    initial: boolean;
    clearBerichtseintraege?: boolean;
  }) {
    const initial = selection.initial;
    const office = this.formGroupService.retrieveOffice(selection.officeId);
    this.isHousingReport = office?.type === OfficeType.GVS;
    this.isKJHReport = office?.type === OfficeType.KJH;
    if (this.isHousingReport) {
      this.subjects = [];
      this.reportEntryCategories = [
        ReportEntryCategory.Bildung,
        ReportEntryCategory.Gesundheit,
        ReportEntryCategory.Standard,
        ReportEntryCategory.AsylUndIntegration,
        ReportEntryCategory.Freizeit,
      ];
    } else if (this.isKJHReport) {
      this.subjects = this.allSubjects.filter((s) => s.value === ReportSubject.Verlaufsdokumentation);
      this.reportEntryCategories = [
        ReportEntryCategory.PsychosozialeEntwicklung,
        ReportEntryCategory.SoziooekonomischeEntwicklung,
        ReportEntryCategory.Bildung,
        ReportEntryCategory.Gesundheit,
        ReportEntryCategory.Umfeld,
      ];
    } else {
      this.subjects = this.allSubjects.filter((s) => s.value !== ReportSubject.Verlaufsdokumentation);
      this.reportEntryCategories = [
        ReportEntryCategory.Bildung,
        ReportEntryCategory.Gesundheit,
        ReportEntryCategory.Standard,
        ReportEntryCategory.AsylUndIntegration,
        ReportEntryCategory.Freizeit,
      ];
    }
    this.reportEntryCategoryOptions = this.enumService.reportEntryCategoryDropdownOptions(this.reportEntryCategories);
    if (!this.subjects.map((s) => s.value).includes(this.formGroup.get("subject").value)) {
      if (this.subjects.length === 1) {
        this.formGroup.get("subject").setValue(this.subjects[0].value);
      } else {
        this.formGroup.get("subject").setValue(null);
      }
    }
    const reportEntries = this.formGroupService.reportEntries;
    for (const reportEntry of reportEntries.controls) {
      if (!this.reportEntryCategories.includes(reportEntry.get("category").value)) {
        if (this.reportEntryCategories.length === 1) {
          reportEntry.get("category").setValue(this.reportEntryCategories[0]);
        } else {
          reportEntry.get("category").setValue(null);
        }
      }
    }

    const type = this.isHousingReport
      ? ReportType.Wohnung
      : this.isKJHReport
        ? ReportType.Verlaufsdokumentation
        : ReportType.Schicht;
    this.formGroup.get("type")?.patchValue(type, { emitEvent: false });

    let employeeIds = [];

    if (selection.officeId) {
      let clientSids: number[] = [];
      if (this.entity) {
        const rIdSet = new Set<number>();
        this.entity.reportEntries.forEach(({ affectedClients }) => affectedClients.forEach((c) => rIdSet.add(c)));
        clientSids = [...rIdSet];
      }
      this.clientService
        .clientControllerFindAllDropdown({
          officeId: selection.officeId,
          housingId: selection.housingId,
          sIds: clientSids,
          initial,
        })
        .subscribe((clients) => {
          this.clients = clients;
        });
      const employeeIdSet = new Set<number>();
      if (this.entity?.mainEmployeeId) {
        employeeIdSet.add(this.entity.mainEmployeeId);
      }
      if (this.entity?.otherEmployees) {
        this.entity.otherEmployees.forEach((i) => employeeIdSet.add(i));
      }
      employeeIds = [...employeeIdSet.values()];
      this.employeeService
        .employeeControllerFindAllDropdown({ officeId: selection.officeId, sIds: employeeIds, initial })
        .subscribe((employees) => {
          this.employees = employees;
        });
      this.housingService
        .housingControllerFindAllDropdown({ officeId: selection.officeId, sIds: [this.entity?.housingId], initial })
        .subscribe((housing) => {
          this.housing = housing;
          if (this.housing.length === 1 && this.formGroupService.housingIdFormControl.value !== this.housing[0].id) {
            this.formGroupService.housingIdFormControl.setValue(this.housing[0].id);
          }
        });
    }

    console.log("reportEntries", reportEntries.at(0)?.value);
    if (!reportEntries.length || selection.clearBerichtseintraege) {
      this.formGroupService.reportEntries.clear();
      if (this.isKJHReport) {
        this.reportEntryCategories.forEach((c) => {
          this.formGroupService.addReportEntry({ category: c }, true);
        });
      } else {
        this.formGroupService.addReportEntry(undefined, true);
      }
    }
  }

  lastUpdatedDate() {
    return [...this.entity?.reportEntries?.map((re) => re?.updatedDate), this.entity?.updatedDate].reduce((a, b) =>
      a > b ? a : b
    );
  }
}
